import { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react'
import { getOriginPath } from './router.js'

/** A screen the admin left by following a link out of it, kept as it was so Back returns to the same place. */
export interface KeptPlace {
  path: string
  scrollY: number
}

export interface AdminTrail {
  /** The screen being shown. */
  path: string
  /** The screens that led here, oldest first. The last one is where Back goes. */
  kept: readonly KeptPlace[]
  /** Where to put the window after this move; null leaves it alone. */
  scrollTo: number | null
}

const CLIENTS_PATH = '/admin'

export const toAdminPath = (pathname: string): string => pathname.replace(/\/$/, '') || CLIENTS_PATH

/**
 * Works out the trail after the address changes. Going to a screen that is
 * on the trail returns to it as it was left; following a link that names
 * the current screen as its origin adds the current screen to the trail;
 * anything else (a menu choice) starts afresh, as moving between screens
 * always has.
 */
export function advanceTrail(trail: AdminTrail, nextPath: string, originPath: string | null, scrollY: number): AdminTrail {
  if (nextPath === trail.path) {
    return trail
  }
  const returningTo = trail.kept.findIndex((place) => place.path === nextPath)
  if (returningTo >= 0) {
    return { path: nextPath, kept: trail.kept.slice(0, returningTo), scrollTo: trail.kept[returningTo]!.scrollY }
  }
  if (originPath === trail.path) {
    return { path: nextPath, kept: [...trail.kept, { path: trail.path, scrollY }], scrollTo: 0 }
  }
  return { path: nextPath, kept: [], scrollTo: null }
}

/** Follows the address bar, in-app links and the browser's own back and forward alike. */
export function useAdminTrail(): AdminTrail {
  const [trail, setTrail] = useState<AdminTrail>(() => ({ path: toAdminPath(window.location.pathname), kept: [], scrollTo: null }))

  useEffect(() => {
    function handleChange(): void {
      const nextPath = toAdminPath(window.location.pathname)
      const origin = getOriginPath()
      const originPath = origin === null ? null : toAdminPath(origin)
      const scrollY = window.scrollY
      setTrail((current) => advanceTrail(current, nextPath, originPath, scrollY))
    }
    window.addEventListener('popstate', handleChange)
    return () => window.removeEventListener('popstate', handleChange)
  }, [])

  useLayoutEffect(() => {
    if (trail.scrollTo !== null) {
      window.scrollTo(0, trail.scrollTo)
    }
  }, [trail])

  return trail
}

const ShownScreenContext = createContext(true)

/** Wraps each admin screen, saying whether it is the one on show or one kept behind it on the trail. */
export const ShownScreenProvider = ShownScreenContext.Provider

/** False inside a screen that is being kept for Back - it is still mounted, so it must not react to the keyboard. */
export function useIsShownScreen(): boolean {
  return useContext(ShownScreenContext)
}
